import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import type {
  FieldMapping,
  FieldMappingEntry,
  ImportEntityType,
  ImportJob,
  MappableField,
} from "@sales-ops-amplify/shared";
import { suggestFieldMapping } from "@sales-ops-amplify/shared";
import {
  createExportJob,
  createImportJob,
  listExportJobs,
  listImportJobs,
  saveImportMapping,
  startImportJob,
} from "@/api/data-exchange";
import {
  SettingsPageHeader,
  SettingsSection,
  SettingsTable,
  SettingsField,
} from "@/components/settings/settings-ui";

type Step = "upload" | "map" | "done";

function modeFromPath(pathname: string): {
  title: string;
  entityType: ImportEntityType;
  isExport: boolean;
} {
  // Match specific segments — do NOT use includes("exports") alone:
  // "/settings/imports-and-exports/…" contains that substring for every import route.
  if (
    /\/imports-and-exports\/exports\/?$/.test(pathname) ||
    pathname.includes("/enriched-csv") ||
    pathname.includes("/csv-export")
  ) {
    return { title: "CSV Exports", entityType: "company", isExport: true };
  }
  if (pathname.includes("/accounts") || pathname.includes("/companies")) {
    return { title: "Account import", entityType: "company", isExport: false };
  }
  return { title: "Lead import", entityType: "person", isExport: false };
}

function sampleForColumn(
  samples: Record<string, string>[] | undefined,
  column: string,
): string {
  if (!samples?.length) return "—";
  return (
    samples
      .map((r) => r[column])
      .filter((v) => v?.trim())
      .slice(0, 2)
      .join(" · ") || "—"
  );
}

export function SettingsImportsExportsPage() {
  const { pathname } = useLocation();
  const mode = useMemo(() => modeFromPath(pathname), [pathname]);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [exportRows, setExportRows] = useState<
    { jobId: string; status: string; rowCount?: number; createdAt: string }[]
  >([]);
  const [step, setStep] = useState<Step>("upload");
  const [dryRun, setDryRun] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [activeJob, setActiveJob] = useState<ImportJob | null>(null);
  const [mapping, setMapping] = useState<FieldMapping | null>(null);
  const [mappableFields, setMappableFields] = useState<MappableField[]>([]);

  const refresh = useCallback(async () => {
    try {
      if (mode.isExport) {
        const { jobs: ex } = await listExportJobs();
        setExportRows(
          ex.map((j) => ({
            jobId: j.jobId,
            status: j.status,
            rowCount: j.rowCount,
            createdAt: j.createdAt,
          })),
        );
      } else {
        const { jobs: list } = await listImportJobs();
        setJobs(list.filter((j) => j.entityType === mode.entityType));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [mode.entityType, mode.isExport]);

  useEffect(() => {
    void refresh();
    setStep("upload");
    setActiveJob(null);
    setMapping(null);
    setFile(null);
    setMessage(null);
    setError(null);
  }, [refresh, mode.entityType, mode.isExport]);

  function updateEntry(csvColumn: string, targetPath: string | null) {
    if (!mapping) return;
    const field = mappableFields.find((f) => f.path === targetPath);
    const entries: FieldMappingEntry[] = mapping.entries.map((e) =>
      e.csvColumn === csvColumn
        ? {
            ...e,
            targetPath,
            transform: field?.type ?? e.transform ?? "string",
          }
        : e,
    );
    setMapping({ ...mapping, entries });
  }

  function onAutoSuggest() {
    if (!activeJob?.headers?.length) return;
    setMapping(suggestFieldMapping(mode.entityType, activeJob.headers));
  }

  async function onUpload() {
    if (!file) {
      setError("Choose a CSV file first");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const csvText = await file.text();
      const created = await createImportJob({
        entityType: mode.entityType,
        filename: file.name,
        contentType: file.type || "text/csv",
        dryRun,
        csvText,
      });
      setActiveJob(created.job);
      setMapping(created.suggestedMapping);
      setMappableFields(created.mappableFields);
      setStep("map");
      setMessage(
        `Parsed ${created.job.headers?.length ?? 0} columns. Map fields, then run import.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmAndRun() {
    if (!activeJob || !mapping) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await saveImportMapping(activeJob.jobId, mapping);
      const { job } = await startImportJob(activeJob.jobId);
      setActiveJob(job);
      setStep("done");
      setMessage(
        `Job ${job.jobId} → ${job.status} (created ${job.counts.created}, updated ${job.counts.updated}, failed ${job.counts.failed}, skipped ${job.counts.skipped})`,
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onExport(entityType: ImportEntityType) {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await createExportJob({ entityType });
      if (result.downloadUrl) {
        window.open(result.downloadUrl, "_blank");
        setMessage(
          `Export ${result.job.jobId} completed (${result.job.rowCount ?? 0} rows)`,
        );
      } else {
        setMessage(`Export ${result.job.jobId} status=${result.job.status}`);
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const fieldsByGroup = useMemo(() => {
    const groups = new Map<string, MappableField[]>();
    for (const f of mappableFields) {
      const g = f.group ?? "Other";
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(f);
    }
    return groups;
  }, [mappableFields]);

  if (mode.isExport) {
    return (
      <div>
        <SettingsPageHeader
          title={mode.title}
          description="Export your Accounts or Leads as canonical CSV v1."
        />
        <SettingsSection title="Run export">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded bg-brand px-3 py-1.5 text-sm text-white disabled:opacity-50"
              disabled={busy}
              onClick={() => void onExport("company")}
            >
              Export accounts
            </button>
            <button
              type="button"
              className="rounded border px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={busy}
              onClick={() => void onExport("person")}
            >
              Export leads
            </button>
          </div>
          {message && <p className="mt-2 text-sm text-muted-foreground">{message}</p>}
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </SettingsSection>
        <SettingsSection title="Recent exports">
          <SettingsTable
            columns={["Job", "Rows", "Status", "Created"]}
            rows={exportRows.map((r) => ({
              Job: r.jobId,
              Rows: String(r.rowCount ?? "—"),
              Status: r.status,
              Created: r.createdAt,
            }))}
            empty="No export jobs yet."
          />
        </SettingsSection>
      </div>
    );
  }

  return (
    <div>
      <SettingsPageHeader
        title={mode.title}
        description={
          mode.entityType === "company"
            ? "Upload an Accounts CSV, map columns to Account fields, then import. Apollo and other exports work via suggested mappings you can override."
            : "Upload a Leads CSV, map columns to Lead fields, then import. Apollo and other exports work via suggested mappings you can override."
        }
      />

      {step === "upload" && (
        <SettingsSection title="1. Upload CSV">
          <SettingsField label="CSV file">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </SettingsField>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            Dry run (validate only, no writes)
          </label>
          <button
            type="button"
            className="mt-3 rounded bg-brand px-3 py-1.5 text-sm text-white disabled:opacity-50"
            disabled={busy || !file}
            onClick={() => void onUpload()}
          >
            {busy ? "Parsing…" : "Parse & map fields"}
          </button>
          {message && <p className="mt-2 text-sm text-muted-foreground">{message}</p>}
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </SettingsSection>
      )}

      {step === "map" && mapping && activeJob && (
        <SettingsSection
          title={
            mode.entityType === "company"
              ? "2. Map columns to Account fields"
              : "2. Map columns to Lead fields"
          }
        >
          <div className="mb-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded border px-3 py-1.5 text-sm"
              onClick={onAutoSuggest}
            >
              Auto-suggest
            </button>
            <button
              type="button"
              className="rounded border px-3 py-1.5 text-sm"
              onClick={() => {
                setStep("upload");
                setActiveJob(null);
                setMapping(null);
              }}
            >
              Back
            </button>
            <button
              type="button"
              className="rounded bg-brand px-3 py-1.5 text-sm text-white disabled:opacity-50"
              disabled={busy}
              onClick={() => void onConfirmAndRun()}
            >
              {busy ? "Importing…" : dryRun ? "Confirm & dry-run" : "Confirm & import"}
            </button>
          </div>
          {message && <p className="mb-2 text-sm text-muted-foreground">{message}</p>}
          {error && <p className="mb-2 text-sm text-destructive">{error}</p>}

          <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-md border border-border [-webkit-overflow-scrolling:touch]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase text-muted-foreground">
                    CSV column
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase text-muted-foreground">
                    Sample
                  </th>
                  <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase text-muted-foreground">
                    Maps to
                  </th>
                </tr>
              </thead>
              <tbody>
                {mapping.entries.map((entry) => (
                  <tr key={entry.csvColumn} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{entry.csvColumn}</td>
                    <td className="max-w-[220px] truncate px-3 py-2 text-muted-foreground">
                      {sampleForColumn(activeJob.sampleRows, entry.csvColumn)}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className="w-full max-w-md rounded border px-2 py-1 text-sm"
                        value={entry.targetPath ?? ""}
                        onChange={(e) =>
                          updateEntry(
                            entry.csvColumn,
                            e.target.value === "" ? null : e.target.value,
                          )
                        }
                      >
                        <option value="">— Skip —</option>
                        {[...fieldsByGroup.entries()].map(([group, fields]) => (
                          <optgroup key={group} label={group}>
                            {fields.map((f) => (
                              <option key={f.path} value={f.path}>
                                {f.label}
                                {f.required ? " *" : ""} ({f.path})
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            * Required for a successful import. Nested paths (e.g. email_data.primary_email)
            write into the {mode.entityType === "company" ? "Account" : "Lead"} entity shape.
          </p>
        </SettingsSection>
      )}

      {step === "done" && (
        <SettingsSection title="Import result">
          {message && <p className="text-sm text-muted-foreground">{message}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <button
            type="button"
            className="mt-3 rounded border px-3 py-1.5 text-sm"
            onClick={() => {
              setStep("upload");
              setActiveJob(null);
              setMapping(null);
              setFile(null);
              setMessage(null);
            }}
          >
            Import another file
          </button>
        </SettingsSection>
      )}

      <SettingsSection title="Import jobs">
        <SettingsTable
          columns={["File", "Rows", "Status", "Created"]}
          rows={jobs.map((j) => ({
            File: j.filename ?? j.jobId,
            Rows: String(j.counts?.total ?? 0),
            Status: j.status,
            Created: j.createdAt,
          }))}
          empty="No import jobs yet."
        />
      </SettingsSection>
    </div>
  );
}
